import React, { useEffect, useState, Fragment, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ChevronDown, Brain, Battery, ShieldCheck, ArrowRight, Smartphone, Activity, Layers, XCircle, Zap, Clock, Target, Eye, Mail, Lock } from 'lucide-react';
// --- Constants & Pixel Logic ---
const CHECKOUT_URL = 'https://www.metodo333.com/cart/52449087979796:1';
const META_PIXEL_ID = '1534608111621678';
declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: any;
  }
}
const trackAddToCart = () => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'AddToCart', {
      content_name: 'Dopamina Zero Bundle',
      content_ids: ['52449087979796'],
      content_type: 'product',
      value: 6990,
      currency: 'ARS'
    });
  }
};
// --- Components ---
const FadeInSection = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.7,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const CTAButton = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <motion.a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" onClick={trackAddToCart} whileHover={{
  scale: 1.02,
  y: -2
}} whileTap={{
  scale: 0.98
}} className={`bg-cyan text-dark font-space font-medium text-lg px-8 py-4 rounded-lg shadow-[0_0_15px_rgba(0,212,255,0.2)] hover:shadow-[0_0_25px_rgba(0,212,255,0.4)] transition-all duration-300 flex items-center justify-center gap-3 w-full md:w-auto cursor-pointer no-underline ${className}`} data-id="element-1">
    {children}
    <ArrowRight className="w-5 h-5" data-id="element-2" />
  </motion.a>;
const SectionSpacer = () => <div className="py-12 md:py-20" data-id="element-3" />;
// --- Main App ---
export function App() {
  // Meta Pixel: Initialize and fire PageView on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.fbq) {
      window.fbq('track', 'PageView');
      return;
    }
    const f: any = window;
    const n: any = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      ;
      (document.head || document.body || document.documentElement).appendChild(script);
    }
    window.fbq?.('init', META_PIXEL_ID);
    window.fbq?.('track', 'PageView');
  }, []);
  // FAQ State
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const faqs = [{
    q: '¿Necesito dejar redes sociales por completo?',
    a: 'No. El objetivo no es aislarte del mundo digital, sino ayudarte a recuperar control sobre cómo lo usas y cuándo lo usas.'
  }, {
    q: '¿Cuánto tiempo necesito al día?',
    a: 'La mayoría de las personas puede aplicar el protocolo dedicando entre 15 y 30 minutos al día, además de hacer pequeños ajustes en su entorno.'
  }, {
    q: '¿Esto funciona si trabajo muchas horas con el celular o computadora?',
    a: 'Sí. Está pensado precisamente para personas que trabajan con pantallas, mensajes, notificaciones y múltiples tareas.'
  }, {
    q: '¿Es un tratamiento médico?',
    a: 'No. Es un protocolo educativo y práctico sobre enfoque, hábitos y entorno digital. No reemplaza atención médica, psicológica ni psiquiátrica.'
  }, {
    q: '¿Cuándo recibo el acceso?',
    a: 'El acceso es inmediato después del pago.'
  }, {
    q: '¿Cómo funciona la garantía?',
    a: 'Tienes 7 días para probar el protocolo. Si sientes que no te aporta claridad, estructura o más control sobre tu atención, puedes pedir un reembolso completo dentro de ese plazo.'
  }];
  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-dark text-gray-300 font-inter selection:bg-cyan/20 selection:text-cyan overflow-x-hidden" data-id="element-4">
      {/* 1. HERO SECTION */}
      <section className="max-w-6xl mx-auto px-6 pt-8 md:pt-24 pb-8" data-id="element-5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center" data-id="element-6">
          <FadeInSection className="order-1 lg:order-1" data-id="element-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-300 text-[11px] md:text-xs font-medium tracking-wide mb-4 md:mb-6 uppercase" data-id="element-8">
              <Activity className="w-3.5 h-3.5 text-cyan" data-id="element-9" />
              <span data-id="element-10">Protocolo práctico de 21 días</span>
            </div>
            <h1 className="text-[28px] sm:text-4xl md:text-5xl lg:text-6xl font-space font-medium text-white leading-[1.1] mb-3 md:mb-6 tracking-tight" data-id="element-11">
              Recupera tu <span className="text-cyan" data-id="element-12">enfoque</span> en 21 días
              sin depender de fuerza de voluntad
            </h1>
            <p className="text-sm md:text-lg text-gray-400 mb-4 md:mb-8 leading-relaxed max-w-xl" data-id="element-13">
              Un sistema práctico para reducir distracciones digitales, bajar la
              niebla mental y volver a sostener trabajo profundo con más
              claridad y menos fatiga.
            </p>

            {/* Urgency line */}
            <div className="flex items-center gap-2 mb-3 md:mb-4" data-id="element-14">
              <span className="inline-flex items-center gap-1.5 text-orange-400 text-xs md:text-sm font-medium" data-id="element-15">
                <span aria-hidden="true" data-id="element-16">⚡</span>
                Precio de lanzamiento — solo por tiempo limitado
              </span>
            </div>

            {/* Primary CTA */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4 md:mb-6" data-id="element-17">
              <CTAButton data-id="element-18">Quiero empezar los 21 días</CTAButton>
              <button onClick={() => scrollToSection('como-funciona')} className="hidden sm:inline-flex px-8 py-4 rounded-lg font-space font-medium text-white border border-white/10 hover:bg-white/5 transition-colors duration-300 items-center justify-center" data-id="element-19">
                Ver cómo funciona
              </button>
            </div>

            {/* Trust strip */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] md:text-xs text-gray-500 mb-5 md:mb-8" data-id="element-20">
              <span className="flex items-center gap-1.5" data-id="element-21">
                <CheckCircle2 className="w-3.5 h-3.5" data-id="element-22" /> Acceso inmediato
              </span>
              <span className="flex items-center gap-1.5" data-id="element-23">
                <Lock className="w-3.5 h-3.5" data-id="element-24" /> Pago único
              </span>
              <span className="flex items-center gap-1.5" data-id="element-25">
                <ShieldCheck className="w-3.5 h-3.5" data-id="element-26" /> Garantía de 7 días
              </span>
            </div>

            {/* Mini social proof — visible above the fold near CTA */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-27">
              {[{
              quote: 'Llevo 14 días sin redes y duermo el doble de bien.',
              name: 'Martín',
              age: '31 años'
            }, {
              quote: 'No creía que fuera posible, en 21 días cambié mi relación con el celular.',
              name: 'Sofía',
              age: '26 años'
            }].map((t, i) => <div key={i} className="bg-dark-card border border-white/5 rounded-xl px-4 py-3" data-id="element-28">
                  <p className="text-gray-300 text-[13px] leading-snug mb-1.5" data-id="element-29">
                    “{t.quote}”
                  </p>
                  <p className="text-[11px] text-gray-500" data-id="element-30">
                    <span className="text-white font-medium" data-id="element-31">{t.name}</span>,{' '}
                    {t.age}
                  </p>
                </div>)}
            </div>

            {/* Feature bullets — desktop only, kept for parity */}
            <ul className="hidden md:grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mt-8" data-id="element-32">
              {['Reduce distracciones digitales', 'Recupera enfoque sostenido', 'Piensa con más claridad', 'Trabaja con menos fatiga mental'].map((item, i) => <li key={i} className="flex items-center gap-2.5 text-gray-300 text-sm" data-id="element-33">
                  <CheckCircle2 className="w-4 h-4 text-cyan shrink-0" data-id="element-34" />
                  <span data-id="element-35">{item}</span>
                </li>)}
            </ul>
          </FadeInSection>

          <FadeInSection delay={0.2} className="hidden lg:block order-2 lg:order-2 relative" data-id="element-36">
            <div className="absolute inset-0 bg-cyan/5 blur-[120px] rounded-full" data-id="element-37" />
            <img src="https://cdn.magicpatterns.com/uploads/x1U2YW7xQFvbjtoePM5hT5/heylloyd-image-1777904934763.png" alt="Dopamina Zero Protocol" className="relative z-10 w-full h-auto rounded-2xl border border-white/5 shadow-2xl object-cover" data-id="element-38" />
          </FadeInSection>
        </div>
      </section>

      <SectionSpacer data-id="element-39" />

      {/* 2. PROBLEMA */}
      <section className="max-w-3xl mx-auto px-6" data-id="element-40">
        <FadeInSection data-id="element-41">
          <h2 className="text-3xl md:text-4xl font-space font-medium text-white mb-8 tracking-tight text-center" data-id="element-42">
            Tu problema no es falta de disciplina.
            <br data-id="element-43" />
            Es sobrecarga constante.
          </h2>
          <div className="text-base md:text-lg text-gray-400 leading-relaxed space-y-5 mb-10" data-id="element-44">
            <p data-id="element-45">
              Si pasas el día saltando entre WhatsApp, correo, notificaciones,
              pestañas abiertas y tareas a medias, no estás solo.
            </p>
            <p data-id="element-46">
              La dificultad para sostener atención, la sensación de niebla
              mental y el cansancio cognitivo son la consecuencia lógica de
              trabajar en un entorno lleno de interrupciones y recompensas
              instantáneas.
            </p>
            <p className="text-white font-medium" data-id="element-47">
              No necesitas exigirte más. Necesitas reducir el ruido y
              reconstruir tu capacidad de concentrarte.
            </p>
          </div>

          <ul className="grid sm:grid-cols-2 gap-3" data-id="element-48">
            {['Empiezas muchas cosas, terminas pocas', 'Cambias de contexto todo el tiempo', 'Te cuesta entrar en trabajo profundo', 'Llegas al final del día con poco avance real'].map((item, i) => <li key={i} className="flex items-start gap-3 bg-dark-card border border-white/5 px-5 py-4 rounded-xl" data-id="element-49">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan mt-2.5 shrink-0" data-id="element-50" />
                <span className="text-gray-300 text-sm" data-id="element-51">{item}</span>
              </li>)}
          </ul>
        </FadeInSection>
      </section>

      <SectionSpacer data-id="element-52" />

      {/* 3. MECANISMO */}
      <section className="max-w-5xl mx-auto px-6" data-id="element-53">
        <FadeInSection data-id="element-54">
          <div className="text-center mb-12" data-id="element-55">
            <h2 className="text-3xl md:text-4xl font-space font-medium text-white mb-4 tracking-tight" data-id="element-56">
              Tu atención está siendo fragmentada todos los días
            </h2>
            <p className="text-lg text-gray-400 max-w-xl mx-auto" data-id="element-57">
              El problema no es tu capacidad. El problema es el entorno.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mb-10" data-id="element-58">
            {[{
            icon: <Smartphone className="w-5 h-5 text-cyan" data-id="element-59" />,
            title: 'Sobreestimulación',
            desc: 'Demasiados estímulos mantienen tu mente en alerta permanente.'
          }, {
            icon: <Layers className="w-5 h-5 text-cyan" data-id="element-60" />,
            title: 'Fragmentación atencional',
            desc: 'Cambiar de tarea cada pocos minutos destruye el enfoque sostenido.'
          }, {
            icon: <Battery className="w-5 h-5 text-cyan" data-id="element-61" />,
            title: 'Fatiga de decisión',
            desc: 'Cada microdecisión diaria consume energía mental antes de que llegues a lo importante.'
          }].map((card, i) => <div key={i} className="bg-dark-card border border-white/5 p-6 rounded-2xl hover:border-white/10 transition-colors" data-id="element-62">
                <div className="bg-dark-lighter w-10 h-10 rounded-lg flex items-center justify-center mb-4 border border-white/5" data-id="element-63">
                  {card.icon}
                </div>
                <h3 className="text-lg font-space font-medium text-white mb-2" data-id="element-64">
                  {card.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed" data-id="element-65">
                  {card.desc}
                </p>
              </div>)}
          </div>

          <p className="text-center text-base md:text-lg text-gray-300 max-w-2xl mx-auto" data-id="element-66">
            Por eso necesitas un sistema claro para recuperar control sobre tu
            atención.
          </p>
        </FadeInSection>
      </section>

      <SectionSpacer data-id="element-67" />

      {/* 4. SOLUCIÓN */}
      <section id="como-funciona" className="max-w-4xl mx-auto px-6 scroll-mt-24" data-id="element-68">
        <FadeInSection data-id="element-69">
          <div className="text-center mb-10" data-id="element-70">
            <h2 className="text-3xl md:text-4xl font-space font-medium text-white mb-6 tracking-tight" data-id="element-71">
              Qué es Dopamina Zero
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed mb-6" data-id="element-72">
              Dopamina Zero es un protocolo práctico de 21 días para ayudarte a
              reducir sobreestimulación digital, reorganizar tu entorno y volver
              a crear bloques reales de enfoque profundo.
            </p>
            <div className="flex flex-col gap-1.5 text-gray-400 text-sm md:text-base" data-id="element-73">
              <p className="flex items-center justify-center gap-2" data-id="element-74">
                <XCircle className="w-4 h-4 text-gray-500" data-id="element-75" /> No es motivación
                vacía.
              </p>
              <p className="flex items-center justify-center gap-2" data-id="element-76">
                <XCircle className="w-4 h-4 text-gray-500" data-id="element-77" /> No es teoría
                abstracta.
              </p>
              <p className="flex items-center justify-center gap-2 text-white font-medium mt-2" data-id="element-78">
                <CheckCircle2 className="w-4 h-4 text-cyan" data-id="element-79" /> Es una estructura
                simple y aplicable para volver a trabajar con claridad.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10" data-id="element-80">
            {['Identificas fugas de atención', 'Ajustas tu entorno digital', 'Recuperas bloques de trabajo profundo', 'Construyes un sistema sostenible'].map((item, i) => <div key={i} className="bg-dark-card border border-white/5 p-5 rounded-xl" data-id="element-81">
                <div className="text-cyan font-space font-medium text-sm mb-2" data-id="element-82">
                  0{i + 1}
                </div>
                <p className="text-gray-300 text-sm leading-relaxed" data-id="element-83">{item}</p>
              </div>)}
          </div>

          <div className="flex justify-center" data-id="element-84">
            <CTAButton data-id="element-85">Sí, quiero empezar el protocolo</CTAButton>
          </div>
        </FadeInSection>
      </section>

      <SectionSpacer data-id="element-86" />

      {/* 5. QUÉ RECIBES */}
      <section className="max-w-4xl mx-auto px-6" data-id="element-87">
        <FadeInSection data-id="element-88">
          <div className="text-center mb-10" data-id="element-89">
            <h2 className="text-3xl md:text-4xl font-space font-medium text-white mb-4 tracking-tight" data-id="element-90">
              Qué recibes al entrar hoy
            </h2>
          </div>

          <div className="bg-dark-card border border-white/10 rounded-3xl p-6 md:p-10" data-id="element-91">
            <div className="space-y-6" data-id="element-92">
              <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start" data-id="element-93">
                <div className="bg-cyan/10 p-3 rounded-xl border border-cyan/20 shrink-0" data-id="element-94">
                  <Brain className="w-7 h-7 text-cyan" data-id="element-95" />
                </div>
                <div data-id="element-96">
                  <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-97">
                    <h3 className="text-lg font-space font-medium text-white" data-id="element-98">
                      Protocolo Dopamina Zero
                    </h3>
                    <span className="text-xs font-medium bg-cyan/10 text-cyan px-2 py-0.5 rounded border border-cyan/20" data-id="element-99">
                      Producto principal
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-100">
                    El sistema paso a paso de 21 días para reducir
                    distracciones, recuperar claridad mental y reconstruir
                    enfoque real.
                  </p>
                </div>
              </div>

              <div className="h-px w-full bg-white/5" data-id="element-101" />

              <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start" data-id="element-102">
                <div className="bg-white/5 p-3 rounded-xl border border-white/10 shrink-0" data-id="element-103">
                  <Smartphone className="w-7 h-7 text-gray-400" data-id="element-104" />
                </div>
                <div data-id="element-105">
                  <h3 className="text-lg font-space font-medium text-white mb-2" data-id="element-106">
                    Bonus 1 — Configuración de entorno digital
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-107">
                    Ajustes prácticos para convertir tu celular y tu computadora
                    en herramientas de trabajo, no en fuentes constantes de
                    interrupción.
                  </p>
                </div>
              </div>

              <div className="h-px w-full bg-white/5" data-id="element-108" />

              <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start" data-id="element-109">
                <div className="bg-white/5 p-3 rounded-xl border border-white/10 shrink-0" data-id="element-110">
                  <Battery className="w-7 h-7 text-gray-400" data-id="element-111" />
                </div>
                <div data-id="element-112">
                  <h3 className="text-lg font-space font-medium text-white mb-2" data-id="element-113">
                    Bonus 2 — Guía de energía y claridad
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-114">
                    Hábitos complementarios para sostener mejor rendimiento
                    mental y menos fatiga cognitiva.
                  </p>
                </div>
              </div>

              <div className="h-px w-full bg-white/5" data-id="element-115" />

              <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start" data-id="element-116">
                <div className="bg-white/5 p-3 rounded-xl border border-white/10 shrink-0" data-id="element-117">
                  <Clock className="w-7 h-7 text-gray-400" data-id="element-118" />
                </div>
                <div data-id="element-119">
                  <h3 className="text-lg font-space font-medium text-white mb-2" data-id="element-120">
                    Bonus 3 — Planner de enfoque profundo
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-121">
                    Plantillas simples para organizar prioridades, reducir
                    fatiga de decisión y proteger tu tiempo de trabajo real.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-gray-500 mt-8 pt-6 border-t border-white/5" data-id="element-122">
              <span className="flex items-center gap-1.5" data-id="element-123">
                <CheckCircle2 className="w-3.5 h-3.5 text-cyan" data-id="element-124" /> Acceso
                inmediato
              </span>
              <span className="flex items-center gap-1.5" data-id="element-125">
                <Lock className="w-3.5 h-3.5 text-cyan" data-id="element-126" /> Pago único
              </span>
              <span className="flex items-center gap-1.5" data-id="element-127">
                <ShieldCheck className="w-3.5 h-3.5 text-cyan" data-id="element-128" /> Garantía de 7
                días
              </span>
            </div>
          </div>
        </FadeInSection>
      </section>

      <SectionSpacer data-id="element-129" />

      {/* 6. RESULTADOS */}
      <section className="max-w-5xl mx-auto px-6" data-id="element-130">
        <FadeInSection data-id="element-131">
          <h2 className="text-3xl md:text-4xl font-space font-medium text-white mb-10 text-center tracking-tight" data-id="element-132">
            Resultados concretos que vas a notar
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10" data-id="element-133">
            {[{
            title: 'Te costará menos empezar tareas importantes',
            icon: <Zap className="w-4 h-4" data-id="element-134" />
          }, {
            title: 'Dejarás de cambiar de app cada pocos minutos',
            icon: <Smartphone className="w-4 h-4" data-id="element-135" />
          }, {
            title: 'Recuperarás bloques reales de trabajo sin interrupciones',
            icon: <Clock className="w-4 h-4" data-id="element-136" />
          }, {
            title: 'Llegarás al final del día con más progreso y menos agotamiento mental',
            icon: <Battery className="w-4 h-4" data-id="element-137" />
          }, {
            title: 'Tendrás más control sobre tus impulsos digitales',
            icon: <Target className="w-4 h-4" data-id="element-138" />
          }, {
            title: 'Pensarás con más claridad y menos ruido mental',
            icon: <Brain className="w-4 h-4" data-id="element-139" />
          }].map((benefit, i) => <div key={i} className="bg-dark-card border border-white/5 p-5 rounded-xl flex items-start gap-3" data-id="element-140">
                <div className="text-cyan mt-0.5 shrink-0" data-id="element-141">{benefit.icon}</div>
                <p className="text-gray-300 text-sm leading-relaxed" data-id="element-142">
                  {benefit.title}
                </p>
              </div>)}
          </div>

          <div className="text-center max-w-xl mx-auto" data-id="element-143">
            <p className="text-base md:text-lg text-gray-300 leading-relaxed" data-id="element-144">
              No se trata solo de producir más.
              <br data-id="element-145" />
              <span className="text-white font-medium" data-id="element-146">
                Se trata de volver a sentir que tu mente trabaja contigo, no
                contra ti.
              </span>
            </p>
          </div>
        </FadeInSection>
      </section>

      <SectionSpacer data-id="element-147" />

      {/* 7. TESTIMONIOS */}
      <section className="max-w-5xl mx-auto px-6" data-id="element-148">
        <FadeInSection data-id="element-149">
          <h2 className="text-3xl md:text-4xl font-space font-medium text-white mb-10 text-center tracking-tight" data-id="element-150">
            Experiencias reales
          </h2>

          <div className="grid md:grid-cols-3 gap-5" data-id="element-151">
            {[{
            name: 'Carlos R.',
            role: 'Desarrollador de software',
            img: 'https://i.pravatar.cc/100?u=carlos',
            before: 'Procrastinaba horas antes de sentarme a programar.',
            after: 'Volvió a completar tareas complejas en la mañana sin pasar media jornada saltando entre distracciones.',
            result: '+3 horas de enfoque al día',
            time: 'Cambios desde día 4'
          }, {
            name: 'Mariana L.',
            role: 'Fundadora de agencia',
            img: 'https://i.pravatar.cc/100?u=mariana',
            before: 'Vivía reaccionando a mensajes, pendientes y urgencias todo el día.',
            after: 'Recuperó calma, claridad y capacidad de estar presente en una sola tarea.',
            result: 'Menos ansiedad, más control mental',
            time: 'Cambios desde 2da semana'
          }, {
            name: 'Diego M.',
            role: 'Diseñador freelance',
            img: 'https://i.pravatar.cc/100?u=diego',
            before: 'Saltaba entre Figma, WhatsApp e Instagram sin ritmo real de trabajo.',
            after: 'Logró sostener bloques de 90 minutos sin revisar el celular constantemente.',
            result: 'Trabajo profundo sostenido',
            time: 'Cambios desde 1ra semana'
          }].map((test, i) => <div key={i} className="bg-dark-card border border-white/5 p-6 rounded-2xl flex flex-col h-full" data-id="element-152">
                <div className="flex items-center gap-3 mb-5" data-id="element-153">
                  <img src={test.img} alt={test.name} className="w-11 h-11 rounded-full border border-white/10" data-id="element-154" />
                  <div data-id="element-155">
                    <h4 className="text-white font-space font-medium text-sm" data-id="element-156">
                      {test.name}
                    </h4>
                    <p className="text-xs text-gray-500" data-id="element-157">{test.role}</p>
                  </div>
                </div>

                <div className="space-y-3 mb-5 flex-grow" data-id="element-158">
                  <div data-id="element-159">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-160">
                      Antes
                    </p>
                    <p className="text-gray-400 text-sm leading-relaxed" data-id="element-161">
                      {test.before}
                    </p>
                  </div>
                  <div data-id="element-162">
                    <p className="text-xs text-cyan uppercase tracking-wider mb-1" data-id="element-163">
                      Después
                    </p>
                    <p className="text-gray-300 text-sm leading-relaxed" data-id="element-164">
                      {test.after}
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-white/5" data-id="element-165">
                  <p className="text-white font-medium text-sm mb-1" data-id="element-166">
                    {test.result}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-167">{test.time}</p>
                </div>
              </div>)}
          </div>
        </FadeInSection>
      </section>

      <SectionSpacer data-id="element-168" />

      {/* 8. FAQ */}
      <section className="max-w-3xl mx-auto px-6" data-id="element-169">
        <FadeInSection data-id="element-170">
          <h2 className="text-3xl font-space font-medium text-white mb-8 text-center tracking-tight" data-id="element-171">
            Preguntas frecuentes
          </h2>
          <div className="space-y-3" data-id="element-172">
            {faqs.map((faq, index) => <div key={index} className="bg-dark-card border border-white/5 rounded-xl overflow-hidden transition-all duration-300" data-id="element-173">
                <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full px-5 md:px-6 py-4 text-left flex items-center justify-between focus:outline-none gap-4" data-id="element-174">
                  <span className="font-space font-medium text-white text-base md:text-lg" data-id="element-175">
                    {faq.q}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform duration-300 shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} data-id="element-176" />
                </button>
                <div className={`px-5 md:px-6 overflow-hidden transition-all duration-300 ease-in-out ${openFaq === index ? 'max-h-60 pb-5 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-177">
                  <p className="text-gray-400 leading-relaxed text-sm md:text-base" data-id="element-178">
                    {faq.a}
                  </p>
                </div>
              </div>)}
          </div>
        </FadeInSection>
      </section>

      <SectionSpacer data-id="element-179" />

      {/* 9. GARANTÍA */}
      <section className="max-w-3xl mx-auto px-6" data-id="element-180">
        <FadeInSection data-id="element-181">
          <div className="bg-dark-card border border-white/10 rounded-2xl p-8 md:p-10 text-center" data-id="element-182">
            <ShieldCheck className="w-10 h-10 text-white/80 mx-auto mb-5" data-id="element-183" />
            <h2 className="text-2xl font-space font-medium text-white mb-3" data-id="element-184">
              Garantía de 7 días
            </h2>
            <p className="text-gray-400 leading-relaxed max-w-xl mx-auto text-sm md:text-base" data-id="element-185">
              Pruébalo durante 7 días. Si sientes que el protocolo no te aporta
              claridad, estructura o control sobre tu atención, puedes pedir un
              reembolso completo dentro de ese plazo.
            </p>
          </div>
        </FadeInSection>
      </section>

      <SectionSpacer data-id="element-186" />

      {/* 10. CIERRE / PRECIO / CTA FINAL */}
      <section className="max-w-3xl mx-auto px-6 pb-20" data-id="element-187">
        <FadeInSection data-id="element-188">
          <div className="bg-dark-card border border-white/10 rounded-3xl p-6 md:p-12 text-center relative overflow-hidden" data-id="element-189">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan/50 to-transparent" data-id="element-190" />

            <h2 className="text-3xl md:text-4xl font-space font-medium text-white mb-4 tracking-tight" data-id="element-191">
              Empieza hoy a recuperar tu enfoque
            </h2>

            <p className="text-gray-400 leading-relaxed max-w-xl mx-auto mb-8 text-sm md:text-base" data-id="element-192">
              Si sientes que tu atención está dispersa, tu energía mental se
              drena demasiado rápido y te cuesta entrar en trabajo profundo,
              este protocolo te da una estructura práctica para volver a tomar
              control.
            </p>

            <div className="flex justify-center items-end gap-2 mb-6" data-id="element-193">
              <span className="text-5xl md:text-6xl font-space font-medium text-white" data-id="element-194">
                $6.990
              </span>
              <span className="text-xl text-gray-500 font-medium mb-2" data-id="element-195">
                ARS
              </span>
            </div>

            <div className="bg-dark-lighter border border-white/5 rounded-xl p-5 mb-6 text-left max-w-md mx-auto" data-id="element-196">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 text-center" data-id="element-197">
                Incluye
              </p>
              <ul className="space-y-2.5" data-id="element-198">
                {['Protocolo Dopamina Zero', 'Bonus 1: Configuración de entorno digital', 'Bonus 2: Guía de energía y claridad', 'Bonus 3: Planner de enfoque profundo'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-300 text-sm" data-id="element-199">
                    <CheckCircle2 className="w-4 h-4 text-cyan shrink-0 mt-0.5" data-id="element-200" />
                    <span data-id="element-201">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-gray-400 mb-8" data-id="element-202">
              <span className="flex items-center gap-1.5" data-id="element-203">
                <Lock className="w-3.5 h-3.5 text-cyan" data-id="element-204" /> Pago único
              </span>
              <span className="flex items-center gap-1.5" data-id="element-205">
                <Clock className="w-3.5 h-3.5 text-cyan" data-id="element-206" /> Acceso inmediato
              </span>
              <span className="flex items-center gap-1.5" data-id="element-207">
                <ShieldCheck className="w-3.5 h-3.5 text-cyan" data-id="element-208" /> Garantía de 7
                días
              </span>
            </div>

            <CTAButton className="mx-auto py-5 px-10 text-lg md:text-xl" data-id="element-209">
              Sí, quiero recuperar mi enfoque
            </CTAButton>
          </div>
        </FadeInSection>
      </section>

      {/* 11. FOOTER */}
      <footer className="border-t border-white/5 bg-dark-card py-10" data-id="element-210">
        <div className="max-w-6xl mx-auto px-6 flex flex-col gap-6" data-id="element-211">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-5" data-id="element-212">
            <div className="text-gray-500 text-sm" data-id="element-213">
              <p className="text-white font-space font-medium mb-1" data-id="element-214">
                Dopamina Zero
              </p>
              <p className="text-xs" data-id="element-215">© 2026 Todos los derechos reservados.</p>
            </div>
          </div>
          <div className="border-t border-white/5 pt-5" data-id="element-216">
            <p className="text-xs text-gray-500 leading-relaxed max-w-3xl" data-id="element-217">
              Este producto tiene fines educativos e informativos. No reemplaza
              atención médica, psicológica ni psiquiátrica profesional.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}